import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { View } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText, PortalOverlay,
} from '../../../../ui';
import { VariousToolTipRootIcon } from '../../../../ui/Icons';
import { useCanvasSize, useElementGeometry } from '../../../../../hooks';

export type MarketingToolTipProps = {
  message: React.ReactNode;
  visible: boolean;
  closeOthers?: boolean;
  testID: string;
  offsetLeft?: number;
  offsetTop?: number;
  maxWidth?: number;
  onMarketingToolTipBackdropClick: () => void;
};

type position = {
  left: number,
  top: number,
};

const ROOT_WIDTH = 24;
const ROOT_HEIGHT = 7;

/** ToolTip
 * Barebones implementation of a tooltip to get FLY-3053 over the
 * finish line. Requires more work to make this more versatile
 * for general usage with things such as edge detection, positioning, and additional
 * customizations
 */
const MarketingToolTip: React.FC<MarketingToolTipProps> = ({
  closeOthers = true,
  offsetLeft = 0,
  offsetTop = 0,
  maxWidth = 250,
  children,
  message,
  testID,
  visible,
  onMarketingToolTipBackdropClick,
}) => {
  const targetRef = useRef(null);
  const tipRef = useRef(null);

  const canvas = useCanvasSize('window');
  const targetGeometry = useElementGeometry(targetRef);
  const tipGeometry = useElementGeometry(tipRef);

  const [tipPosition, setTipPosition] = useState<position>({ left: null, top: null });
  const [rootPosition, setRootPosition] = useState<position>({ left: null, top: null });

  const updateRootPosition = (target): void => {
    const left = ((target.width / 2) + target.left) - (ROOT_WIDTH / 2) + offsetLeft;
    const top = target.top - ROOT_HEIGHT + offsetTop;

    setRootPosition({ left, top });
  };

  const updateTipPosition = (target, tip): void => {
    const tempLeft = ((target.width / 2) + target.left) - (tip.width / 2) + offsetLeft;
    const tempTop = target.top - tip.height - ROOT_HEIGHT + offsetTop;

    const top = tempTop > 0 ? tempTop : 0;
    let left = 0;
    if (tempLeft > 0) {
      left = tempLeft + tip.width < canvas.width ? tempLeft : canvas.width - tip.width;
    }

    setTipPosition({ left, top });
  };

  useEffect(() => {
    if (!canvas || !targetGeometry || !tipGeometry) return;
    updateRootPosition(targetGeometry);
    updateTipPosition(targetGeometry, tipGeometry);
  }, [canvas, targetGeometry, tipGeometry]);

  return (
    <React.Fragment>
      <View ref={targetRef} testID={`${testID}-marketing-tool-tip-target`}>
        {children}
      </View>
      {visible && (
        <PortalOverlay
          name={`${testID}-marketing-tool-tip-overlay`}
          onBackdropClick={onMarketingToolTipBackdropClick}
          isTransparent
          closeOthers={closeOthers}
          animationStyle="fadeIn"
        >
          <Box
            paddingTop={8}
            paddingX={16}
            position="absolute"
            maxWidth={maxWidth}
            left={tipPosition.left}
            top={tipPosition.top}
            ref={tipRef}
          >
            <Box
              testID={`${testID}-marketing-tool-tip`}
              color={colors.purple}
              borderRadius={8}
              borderColor={colors.purple}
              borderWidth={1}
              borderStyle="solid"
              padding={8}
            >
              <Inline
                alignItems="flex-start"
                justifyContent="space-between"
                flexGrow={1}
                flexShrink={1}
                flexBasis="auto"
                space={12}
              >
                <BodyText
                  appearance="small"
                  weight="bold"
                  color={colors.whiteWash}
                  testID={`${testID}-marketing-tool-tip-message`}
                >
                  {message}
                </BodyText>
              </Inline>
            </Box>
          </Box>
          <Box
            testID={`${testID}-marketing-tool-tip-root`}
            left={rootPosition.left}
            top={rootPosition.top}
            position="absolute"
          >
            <VariousToolTipRootIcon />
          </Box>
        </PortalOverlay>
      )}
    </React.Fragment>
  );
};

export default MarketingToolTip;
